import React, { useState } from 'react';
import { useDashboardPresenter, Machine } from './presenter';
import { DashboardView } from './view';
import { machinesApi } from '../../../lib/apis';

export const DashboardPage: React.FC = () => {
  const { state } = useDashboardPresenter();
  const [itemsByMachine, setItemsByMachine] = useState<Record<string, any[]>>({});
  const [loadingByMachine, setLoadingByMachine] = useState<Record<string, boolean>>({});
  const [errorByMachine, setErrorByMachine] = useState<Record<string, string | null>>({});

  // Fetch products for each machine as soon as machines are loaded
  React.useEffect(() => {
    const fetchAll = async () => {
      const machines = state.machines || [];
      if (!machines.length) return;
      const newLoading: Record<string, boolean> = {};
      const newErrors: Record<string, string | null> = {};
      machines.forEach(m => { newLoading[m.id] = true; newErrors[m.id] = null; });
      setLoadingByMachine(prev => ({ ...prev, ...newLoading }));
      setErrorByMachine(prev => ({ ...prev, ...newErrors }));

      await Promise.all(machines.map(async (m) => {
        try {
          const res = await machinesApi.get<{ data: any[] }>(`/machines/${m.id}/products`);
          const list = (res as any)?.data ?? (res as any) ?? [];
          setItemsByMachine(prev => ({ ...prev, [m.id]: list }));
        } catch (e: any) {
          setErrorByMachine(prev => ({ ...prev, [m.id]: e.message || 'Failed to load products' }));
        } finally {
          setLoadingByMachine(prev => ({ ...prev, [m.id]: false }));
        }
      }));
    };
    fetchAll();
  }, [state.machines]);

  // Real-time stock updates via SSE
  React.useEffect(() => {
    // Prefer explicit notification base, then API base, then default to backend behind /admin/api on same origin
    const notifBase = (
      process.env.REACT_APP_NOTIFICATIONS_BASE ||
      process.env.REACT_APP_API_BASE ||
      (window.location && window.location.origin ? window.location.origin + '/admin/api' : 'http://localhost:3002')
    ).toString();
    const url = notifBase.replace(/\/$/, '') + '/notifications/stream';
    const es = new EventSource(url);

    const onStockUpdate = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data || '{}') as { machineId: string; updates: { productId: string; stock: number }[] };
        if (!data || !data.machineId || !Array.isArray(data.updates)) return;
        setItemsByMachine(prev => {
          const current = prev[data.machineId] || [];
          if (current.length === 0) return prev; // nothing loaded to patch
          const updatesMap = new Map<string, number>(data.updates.map(u => [String(u.productId), Number(u.stock)]));
          const patched = current.map(it => updatesMap.has(String(it.id)) ? { ...it, stock: updatesMap.get(String(it.id)) } : it);
          return { ...prev, [data.machineId]: patched };
        });
      } catch {}
    };

    es.addEventListener('stock_update', onStockUpdate as any);

    return () => {
      es.removeEventListener('stock_update', onStockUpdate as any);
      es.close();
    };
  }, []);


  const rowDetail = (m: Machine) => {
    const items = itemsByMachine[m.id] || [];
    const loading = !!loadingByMachine[m.id];
    const error = errorByMachine[m.id] || null;
    return (
      <div className="p-4 bg-muted/5 rounded-lg border border-border/30 m-2 transition-all duration-500 ease-in-out">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-base font-semibold tracking-tight text-foreground">Inventory Breakdown</h4>
            <p className="text-xs text-muted-foreground">Current products in {m.name}</p>
          </div>
        </div>
        {error && <div className="mb-4 p-3 rounded-md bg-destructive/10 text-sm text-destructive border border-destructive/20">{error}</div>}
        {loading ? (
          <div className="flex items-center justify-center p-6 animate-in fade-in duration-500">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center bg-card/30 rounded-lg border border-dashed border-border/50 animate-in fade-in duration-500">
            <span className="text-sm font-medium text-muted-foreground">No products found</span>
            <span className="text-xs text-muted-foreground mt-1">This machine currently has no inventory.</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 animate-in fade-in zoom-in-95 duration-500">
            {items.map((it, i) => {
              const stock = Number(it.stock ?? 0);
              const isLowStock = stock > 0 && stock <= 5;
              const isOutOfStock = stock === 0;
              return (
                <div key={it.id ?? it.sku ?? i} className="group relative overflow-hidden rounded-xl border bg-card/60 backdrop-blur-sm p-4 shadow-sm transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-lg hover:bg-card">
                  <div className={`absolute top-0 left-0 w-1 h-full ${isOutOfStock ? 'bg-destructive' : isLowStock ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                  <div className="flex flex-col h-full justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold leading-tight line-clamp-2">{it.name}</div>
                      <div className="text-xs text-muted-foreground mt-1 font-mono">SKU: {it.sku || '-'}</div>
                    </div>
                    <div className="flex items-end justify-between border-t border-border/50 pt-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Stock</div>
                      <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        isOutOfStock ? 'bg-destructive/10 text-destructive' : 
                        isLowStock ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400' : 
                        'bg-green-500/10 text-green-600 dark:text-green-400'
                      }`}>
                        {stock}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardView
      machines={state.machines}
      loading={state.loading}
      error={state.error}
      summaries={state.summaries}
      productCounts={state.productCounts}
      rowDetail={rowDetail}
    />
  );
};

export default DashboardPage;
